import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { TopBar } from "../components/Shell";
import { Banner, Button, Card } from "../components/ui";
import type { Key } from "../locales/en";

// One page for every emailed accept link: staff invitations, ownership
// transfers, partner store transfers and organization invitations.
const kinds = {
  invitation: { endpoint: "/admin/v1/invitations/accept", title: "accept.invite_title", body: "accept.invite_body" },
  ownership: { endpoint: "/admin/v1/ownership-transfers/accept", title: "accept.transfer_title", body: "accept.transfer_body" },
  transfer: { endpoint: "/admin/v1/transfers/accept", title: "accept.transfer_title", body: "accept.transfer_body" },
  organization: { endpoint: "/admin/v1/organizations/invitations/accept", title: "accept.invite_title", body: "accept.invite_body" },
} satisfies Record<string, { endpoint: string; title: Key; body: Key }>;

export function Accept({ kind }: { kind: keyof typeof kinds }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { token } = useSearch({ strict: false }) as { token?: string };
  const k = kinds[kind];
  const accept = useMutation({
    mutationFn: () => api<{ store_id?: string; org_id?: string }>(k.endpoint, { body: { token } }),
    onSuccess: async (r) => {
      await qc.invalidateQueries();
      if (r.org_id && kind === "organization") navigate({ to: "/partners/$org", params: { org: r.org_id } });
      else navigate({ to: "/stores" });
    },
  });
  return (
    <div className="min-h-full">
      <TopBar />
      <div className="mx-auto max-w-md px-4 py-16">
        <Card title={t(k.title)}>
          <div className="space-y-4">
            <p className="text-sm text-stone-700">{t(k.body)}</p>
            {accept.error && <Banner tone="critical">{accept.error.message}</Banner>}
            <Button variant="primary" disabled={!token} loading={accept.isPending} onClick={() => accept.mutate()}>{t("accept.accept")}</Button>
          </div>
        </Card>
      </div>
    </div>
  );
}
